import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { supabase } from '@/lib/supabase';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';
import type { PulseRow } from '@/lib/pulse/api';

type Range = '7d' | '30d' | '90d';
const DAYS: Record<Range, number> = { '7d': 7, '30d': 30, '90d': 90 };

interface Props {
  orgId: string;
  myPulses: PulseRow[];
}

type OrgDailyRow = { day: string; avg_pulse: number; n_employees: number };

interface Point {
  day: string;
  label: string;
  you: number | null;
  org: number | null;
}

function buildSeries(
  days: number,
  myPulses: PulseRow[],
  orgRows: OrgDailyRow[],
): Point[] {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const youBy = new Map<string, number>();
  for (const p of myPulses) youBy.set(p.created_day, p.value);
  const orgBy = new Map<string, number>();
  for (const r of orgRows) orgBy.set(r.day, Number(r.avg_pulse));

  const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
  const out: Point[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({
      day: key,
      label: fmt.format(d),
      you: youBy.has(key) ? youBy.get(key)! : null,
      org: orgBy.has(key) ? orgBy.get(key)! : null,
    });
  }
  return out;
}

function avg(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function ChartTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const items = payload.filter((p: any) => p.value != null);
  if (!items.length) return null;
  const day = payload[0]?.payload?.day as string | undefined;
  const longLabel = day
    ? new Intl.DateTimeFormat(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).format(new Date(day + 'T00:00:00Z'))
    : '';
  return (
    <div className="rounded-md border border-border bg-popover/95 backdrop-blur px-3 py-2 shadow-md text-xs">
      <div className="font-medium text-foreground mb-1">{longLabel}</div>
      <ul className="space-y-0.5">
        {items.map((p: any) => (
          <li key={p.dataKey} className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} />
            <span className="text-muted-foreground">{p.name}</span>
            <span className="ml-auto tabular-nums text-foreground">
              {Number(p.value).toFixed(1)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function YouVsOrgChart({ orgId, myPulses }: Props) {
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>('30d');
  const days = DAYS[range];

  const orgQuery = useQuery({
    queryKey: ['employee_org_pulse_daily', orgId, days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('employee_org_pulse_daily', {
        _org_id: orgId,
        _days: days,
      });
      if (error) throw error;
      return (data ?? []) as OrgDailyRow[];
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const series = useMemo(
    () => buildSeries(days, myPulses, orgQuery.data ?? []),
    [days, myPulses, orgQuery.data],
  );

  // 14-day summary (always 14d regardless of selected range, per spec)
  const last14 = series.slice(-14);
  const you14 = avg(last14.map((p) => p.you).filter((v): v is number => v != null));
  const org14 = avg(last14.map((p) => p.org).filter((v): v is number => v != null));
  const hasYou = myPulses.length > 0;

  const gap = you14 != null && org14 != null ? you14 - org14 : null;
  let gapLabel = '';
  if (gap != null) {
    if (Math.abs(gap) < 0.05) gapLabel = 'tracking with team';
    else if (gap > 0) gapLabel = `+${gap.toFixed(1)} above`;
    else gapLabel = `${gap.toFixed(1)} below`;
  }

  let promptText = '';
  let promptCta: { label: string; onClick: () => void } | null = null;
  if (!hasYou) {
    promptText = 'No check-ins yet — your first check-in starts your trend.';
  } else if (gap == null) {
    promptText = 'Not enough team data yet to compare.';
  } else if (gap >= 0.5) {
    promptText = "You're consistently above your team's average — what's working for you?";
  } else if (gap <= -0.5) {
    promptText = "You're trending below your team. Want to talk it through with Charles?";
    promptCta = {
      label: 'Open Charles',
      onClick: () => navigate('/app/charles', { state: { topic: 'well-being' } }),
    };
  } else {
    promptText = "You're tracking close to your team's average.";
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          {hasYou && you14 != null && org14 != null ? (
            <>
              <span className="text-muted-foreground">14d avg · </span>
              <span className="font-medium text-foreground">You {you14.toFixed(1)}</span>
              <span className="text-muted-foreground"> / Org {org14.toFixed(1)}</span>
              <span className="text-muted-foreground"> · {gapLabel}</span>
            </>
          ) : hasYou && org14 == null ? (
            <span className="text-muted-foreground">14d avg · You {you14?.toFixed(1) ?? '—'} · team data pending (n &lt; 5)</span>
          ) : (
            <span className="text-muted-foreground">{promptText}</span>
          )}
        </div>
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={range}
          onValueChange={(v) => v && setRange(v as Range)}
        >
          <ToggleGroupItem value="7d">7d</ToggleGroupItem>
          <ToggleGroupItem value="30d">30d</ToggleGroupItem>
          <ToggleGroupItem value="90d">90d</ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 8, right: 12, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              domain={[1, 10]}
              ticks={[1, 5, 10]}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend
              iconType="circle"
              wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
              formatter={(v) => <span className="text-muted-foreground">{v}</span>}
            />
            <Line
              type="monotone"
              dataKey="org"
              name="Your team"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              connectNulls
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="you"
              name="You"
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              dot={{ r: 2.5, fill: 'hsl(var(--foreground))', strokeWidth: 0 }}
              activeDot={{ r: 4.5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {hasYou && (
        <div className="rounded-md border border-border bg-muted/30 p-3 flex items-start justify-between gap-3">
          <p className="text-sm text-foreground">{promptText}</p>
          {promptCta && (
            <Button size="sm" variant="default" onClick={promptCta.onClick} className="gap-1.5 shrink-0">
              <MessageCircle className="h-3.5 w-3.5" />
              {promptCta.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
